import type { LifestylePrize } from "@/app/redux/features/api/lifestyle.types";

export interface SearchSuggestion {
  /** The text that lands in the search box when this is chosen. */
  value: string;
  kind: "make" | "prize";
}

/** The design's dropdown shows eight rows; Angular's was unbounded. */
export const SUGGESTION_LIMIT = 8;

/**
 * Matching makes first, then matching "Make Model" titles, both in data order.
 *
 * Ported from `getSearchSuggestions` and `suggestResult`. The Angular version
 * also collected styles, but its dedupe callback never returned a value, so
 * `uniqueStyles` came back empty and no style ever reached the dropdown — which
 * is why the design lists makes and prize titles only. Kept as it behaved.
 */
export function buildSuggestions(prizes: LifestylePrize[], term: string, limit = SUGGESTION_LIMIT): SearchSuggestion[] {
  const query = term.trim().toLowerCase();
  if (query === "") return [];

  const [makeTerm, modelTerm] = query.split(/\s+/);
  const seen = new Set<string>();
  const makes: SearchSuggestion[] = [];
  const titles: SearchSuggestion[] = [];

  for (const prize of prizes) {
    const make = prize.Make ?? "";
    if (!make || seen.has(make)) continue;
    if (!make.toLowerCase().includes(query)) continue;

    seen.add(make);
    makes.push({ value: make, kind: "make" });
  }

  for (const prize of prizes) {
    const make = prize.Make ?? "";
    const model = prize.Model ?? "";
    const title = `${make} ${model}`.trim();
    if (!title || seen.has(title)) continue;

    // One term matches either half; two are read as "make model", as in Angular.
    const matches = modelTerm
      ? make.toLowerCase().includes(makeTerm) && model.toLowerCase().includes(modelTerm)
      : make.toLowerCase().includes(query) || model.toLowerCase().includes(query);
    if (!matches) continue;

    seen.add(title);
    titles.push({ value: title, kind: "prize" });
  }

  return [...makes, ...titles].slice(0, limit);
}

export interface LabelPart {
  text: string;
  isMatch: boolean;
}

/**
 * Splits a label around every case-insensitive hit of the term so the matched
 * run can be tinted. Angular built this as an HTML string and pushed it through
 * `$sce.trustAsHtml`, which would mean rendering CMS copy as markup.
 */
export function highlightParts(label: string, term: string): LabelPart[] {
  const needle = term.trim().toLowerCase();
  if (needle === "") return [{ text: label, isMatch: false }];

  const parts: LabelPart[] = [];
  const haystack = label.toLowerCase();
  let cursor = 0;

  for (let hit = haystack.indexOf(needle); hit !== -1; hit = haystack.indexOf(needle, cursor)) {
    if (hit > cursor) parts.push({ text: label.slice(cursor, hit), isMatch: false });
    parts.push({ text: label.slice(hit, hit + needle.length), isMatch: true });
    cursor = hit + needle.length;
  }

  if (cursor < label.length) parts.push({ text: label.slice(cursor), isMatch: false });
  return parts;
}
